import { useLocation, useNavigate } from "react-router-dom";
import { Menu, ChevronRight, LogOut, ShieldCheck } from "lucide-react";
import { NAV_ITEMS } from "@/data/nav";
import { useUser } from "@/context/UserContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LaunchHeaderBadge } from "@/components/launch/LaunchHeaderBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_LABELS = {
  administrator: "Administrator",
  editor: "Editor",
  viewer: "Viewer",
};

export const Header = ({ onOpenMobileNav }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useUser();

  const active =
    [...NAV_ITEMS]
      .filter((n) => (n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)))
      .sort((a, b) => b.to.length - a.to.length)[0] || NAV_ITEMS[0];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (!currentUser) return null;
  const initials = (currentUser.name || currentUser.email || "?").slice(0, 1).toUpperCase();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-[#E4E9F3] bg-white/90 px-4 backdrop-blur-md md:px-8"
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          onClick={onOpenMobileNav}
          data-testid="mobile-nav-trigger"
          className="flex h-9 w-9 items-center justify-center rounded-xl text-[#14245F] transition-colors hover:bg-[#EEF2FB] md:hidden"
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        <nav className="flex min-w-0 items-center gap-1.5 text-sm">
          <span className="hidden font-semibold text-[#5A6B8C] sm:inline">War Room</span>
          <ChevronRight size={14} className="hidden text-[#9DAAC6] sm:inline" />
          <span
            className="truncate font-heading text-base font-semibold"
            style={{ color: "var(--accent-primary)" }}
          >
            {active.label}
          </span>
        </nav>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        <LaunchHeaderBadge />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex items-center gap-3 rounded-full border border-[#E4E9F3] bg-white py-1 pl-1 pr-3 transition-colors hover:border-[var(--accent-primary)]/50"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback
                  className="text-xs font-bold text-white"
                  style={{ backgroundColor: "var(--accent-primary)" }}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden text-left leading-tight sm:block">
                <p className="text-sm font-bold text-[#14245F]">{currentUser.name}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5A6B8C]">
                  {ROLE_LABELS[currentUser.role] || currentUser.role}
                </p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60" data-testid="user-menu">
            <DropdownMenuLabel className="leading-tight">
              <p className="text-sm font-bold text-[#14245F]">{currentUser.name}</p>
              <p className="truncate text-[11px] font-normal text-[#5A6B8C]">{currentUser.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-[#5A6B8C]">
              <ShieldCheck size={14} style={{ color: "var(--accent-primary)" }} />
              {ROLE_LABELS[currentUser.role] || currentUser.role}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex items-center gap-2 py-2 text-[#DC2626] focus:text-[#DC2626]"
            >
              <LogOut size={16} />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
